import { useState } from 'react';
import { supabase } from '../supabaseClient';

const C = {
  blue: '#6699ff',
  creamWarm: '#ede5d8',
  creamPale: '#f8f5ef',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

const ALLOWED_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
};

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export default function FileUploader({ articleId, files, onFilesChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES[file.type]) {
      return `"${file.name}" — file type not allowed. Accepted: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG, WebP`;
    }
    if (file.size > MAX_SIZE) {
      return `"${file.name}" — file too large. Maximum size is 10MB`;
    }
    const ext = file.name.split('.').pop().toLowerCase();
    const dangerousExts = ['exe', 'bat', 'cmd', 'sh', 'js', 'vbs', 'ps1', 'msi', 'dll'];
    if (dangerousExts.includes(ext)) {
      return `"${file.name}" — file type blocked for security reasons`;
    }
    return null;
  };

  const uploadFiles = async (fileList) => {
    const filesToUpload = Array.from(fileList);
    const errors = [];

    for (const file of filesToUpload) {
      const err = validateFile(file);
      if (err) { errors.push(err); continue; }
    }

    if (errors.length) {
      alert('Upload errors:\n\n' + errors.join('\n'));
      if (errors.length === filesToUpload.length) return;
    }

    setUploading(true);
    const newFiles = [];

    for (const file of filesToUpload) {
      if (validateFile(file)) continue;

      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${articleId}/${Date.now()}_${safeName}`;

      const { error } = await supabase.storage
        .from('article-files')
        .upload(filePath, file, { contentType: file.type });

      if (error) {
        errors.push(`"${file.name}" — upload failed: ${error.message}`);
        continue;
      }

      const { error: dbError } = await supabase.from('article_files').insert({
        article_id: articleId,
        file_name: file.name,
        file_path: filePath,
        file_type: file.type,
        file_size: file.size,
      });

      if (!dbError) {
        newFiles.push({
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
        });
      }
    }

    if (errors.length) alert('Some files had issues:\n\n' + errors.join('\n'));
    if (newFiles.length) onFilesChange([...files, ...newFiles]);
    setUploading(false);
  };

  const removeFile = async (filePath, index) => {
    await supabase.storage.from('article-files').remove([filePath]);
    await supabase.from('article_files').delete().eq('file_path', filePath);
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.multiple = true;
          input.accept = Object.values(ALLOWED_TYPES).join(',');
          input.onchange = (e) => uploadFiles(e.target.files);
          input.click();
        }}
        style={{
          border: `2px dashed ${dragOver ? C.blue : C.creamWarm}`,
          background: dragOver ? 'rgba(102,153,255,0.04)' : C.creamPale,
          padding: '36px 24px', textAlign: 'center', cursor: 'pointer',
          transition: 'all 0.2s ease', marginBottom: files.length ? 16 : 0,
        }}
      >
        {uploading ? (
          <div style={{ fontSize: 14, color: C.blue, fontWeight: 600 }}>Uploading...</div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: C.textBody, fontWeight: 500, marginBottom: 6 }}>
              Drop files here or click to browse
            </div>
            <div style={{ fontSize: 11, color: C.textMuted }}>
              PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG, WebP — Max 10MB each
            </div>
          </>
        )}
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {files.map((file, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', background: C.white, border: `1px solid ${C.creamWarm}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  padding: '2px 8px', background: 'rgba(102,153,255,0.08)',
                  fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: 1,
                  textTransform: 'uppercase',
                }}>
                  {file.file_name.split('.').pop()}
                </span>
                <span style={{ fontSize: 13, color: C.textDark, fontWeight: 500 }}>{file.file_name}</span>
                <span style={{ fontSize: 11, color: C.textMuted }}>{formatSize(file.file_size)}</span>
              </div>
              <button
                onClick={() => removeFile(file.file_path, i)}
                style={{
                  background: 'none', border: 'none', color: '#dc2626',
                  fontSize: 12, cursor: 'pointer', fontWeight: 600,
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}